/**
 * static/js/utils/viewport.js
 *
 * Viewport / breakpoint helpers. matchMedia wrappers tied to Tailwind's default
 * breakpoints, plus a `--vh` JS-side fallback for the rare browsers without
 * `100dvh` support.
 *
 * Loaded per-page (not in base.html). Pure utilities — no auto-side-effects.
 *
 * Usage:
 *   if (JN.viewport.isMobile()) { ... }
 *   const off = JN.viewport.onBreakpointChange('lg', isLg => { ... });
 *   off(); // unsubscribe
 *   JN.viewport.autoUpdateVhProperty(); // sets --vh and updates on resize
 *
 * Refs #3886 (Mobile UI/UX Phase 2 PR 2.1).
 */
(function (window, document) {
  'use strict';

  // Tailwind default breakpoints. Source of truth: tailwind.config.js (no overrides).
  var BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

  function isAtLeast(bp) {
    return window.matchMedia('(min-width: ' + BREAKPOINTS[bp] + 'px)').matches;
  }
  function isBelow(bp) {
    return window.matchMedia('(max-width: ' + (BREAKPOINTS[bp] - 1) + 'px)').matches;
  }
  function onBreakpointChange(bp, callback) {
    var mq = window.matchMedia('(min-width: ' + BREAKPOINTS[bp] + 'px)');
    function handler(e) { callback(e.matches); }
    mq.addEventListener('change', handler);
    callback(mq.matches); // fire once immediately with current state
    return function unsubscribe() { mq.removeEventListener('change', handler); };
  }

  // --vh CSS custom property. Use in CSS as `height: calc(var(--vh, 1vh) * 100)`.
  // 100dvh (CSS spec) is the modern equivalent and should be preferred where
  // browser support allows; this is a fallback for older targets only.
  function setVhProperty() {
    document.documentElement.style.setProperty('--vh', (window.innerHeight * 0.01) + 'px');
  }
  function autoUpdateVhProperty() {
    setVhProperty();
    window.addEventListener('resize', setVhProperty);
    window.addEventListener('orientationchange', setVhProperty);
  }

  window.JN = window.JN || {};
  window.JN.viewport = {
    BREAKPOINTS: BREAKPOINTS,
    isMobile: function () { return isBelow('md'); },
    isDesktop: function () { return isAtLeast('lg'); },
    isAtLeast: isAtLeast,
    isBelow: isBelow,
    onBreakpointChange: onBreakpointChange,
    setVhProperty: setVhProperty,
    autoUpdateVhProperty: autoUpdateVhProperty,
  };
})(window, document);
